import { CSSObject, Theme } from '@mui/material'
import { Row } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMergeFormData } from 'components/Form'
import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { useErrorsOnPagesInfo } from 'features/globalSetup/hooks/useErrorsOnPagesInfo'
import { useFooterInfoProps } from 'features/globalSetup/hooks/useFooterInfoProps'
import { calculatePagination } from 'providers/tanstack-query/table'
import { Schema } from '../../form/schema'
import { AdUnitBidderSlice, useGetAdUnitBidders } from '../../hooks/UseGetAdUnitBidders'
import { useColumns } from './columns/useColumns'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useCollectInventoryConnectionDirtyRows } from './hooks/useCollectInventoryConnectionDirtyRows'
import { AdUnitsForTable, AdUnitSortBy, AdUnitsTableProps, Params, paramsDefaults } from './types'
import { adUnitBiddersToSchema, getRequiredParams, getRequiredParamsNames } from './utils'

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const AdUnitsTable = ({
  domainBidder,
  filters,
  showOptionalParams,
}: AdUnitsTableProps): React.JSX.Element => {
  const [params, setParams] = useState<Params>(paramsDefaults)
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.edit.bidParamsAndConnection.adUnitsTable.adUnits',
  })

  const {
    domain: { id: domainId },
    workspaceBidder: {
      bidder: { s2sAvailable },
      status,
    },
  } = domainBidder

  const { page, rowsPerPage, setPage, setRowsPerPage } = usePagination({
    resetOnChange: [domainId, filters],
  })
  const { props: sortProps, sort } = useSort<AdUnitsForTable, AdUnitSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { adUnitBidders, count, loading } = useGetAdUnitBidders({
    domainBidder,
    ...calculatePagination({ page, rowsPerPage }),
    filters,
    sort,
  })

  useEffect(() => {
    if (adUnitBidders.length === 0) return

    const { csParams, s2sParams } = adUnitBidders[0]

    const visibleCsParams = showOptionalParams ? csParams : getRequiredParams(csParams)
    const visibleS2sParams = showOptionalParams ? s2sParams : getRequiredParams(s2sParams)

    setParams({
      cs: {
        csParams: visibleCsParams,
        csRequiredParamsNames: getRequiredParamsNames(visibleCsParams),
      },
      s2s: {
        s2sParams: s2sAvailable ? visibleS2sParams : undefined,
        s2sRequiredParamsNames: getRequiredParamsNames(visibleS2sParams),
      },
    })
  }, [adUnitBidders, showOptionalParams])

  const columns = useColumns({ ...params, adUnitBidders, workspaceStatus: status })

  useMergeFormData<SchemaSlice>({
    data: adUnitBiddersToSchema(adUnitBidders),
    path: 'adUnitBidders',
    resetDependency: domainId,
    resetValue: {},
  })

  useCollectInventoryConnectionDirtyRows()

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  }

  const { pagesWithError } = useErrorsOnPagesInfo<SchemaSlice>({
    current: { page, rowsPerPage },
    ids: adUnitBidders.map((el: AdUnitBidderSlice): string => el.id),
    schemaPath: 'adUnitBidders',
    updateDependency: adUnitBidders,
  })
  const footerInfoProps = useFooterInfoProps({ pagesWithError })

  const handleStyleRow = (row: Row<AdUnitsForTable>, theme: Theme): CSSObject => {
    const {
      original: {
        adUnit: { ybAdUnitEnabled },
      },
    } = row

    let styles: CSSObject = {}
    if (ybAdUnitEnabled) {
      styles = {
        ...styles,
        backgroundColor: theme.palette.cream.main,
      }
    }

    return styles
  }

  return (
    <Table<AdUnitsForTable>
      columns={columns}
      data={adUnitBidders}
      footerInfo={footerInfoProps}
      loading={loading || (adUnitBidders.length > 0 && params.cs.csParams === undefined)}
      meta={{
        stickyHeader: true,
        styleRow: handleStyleRow,
        verticalBorders: true,
      }}
      notFoundMessage={t('noAdUnits')}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
